import React, { useState, useRef } from 'react';
import { Upload, X, FileText } from 'lucide-react';
import { ACCEPTED_FILE_TYPES, MAX_FILE_SIZE_BYTES } from '../../data/partnerOptions';

// ============================================================
// Reusable inquiry form building blocks.
// ============================================================

export const FieldWrapper = ({ label, required, helper, children, testId }) => (
  <div className="space-y-1.5" data-testid={testId}>
    <label className="block text-sm font-semibold text-gray-900">
      {label}
      {required && <span className="text-orange-600 ml-1">*</span>}
    </label>
    {helper && <p className="text-xs text-gray-500 leading-snug">{helper}</p>}
    {children}
  </div>
);

export const TextField = ({ label, required, helper, value, onChange, placeholder, type = 'text', name, testId }) => (
  <FieldWrapper label={label} required={required} helper={helper} testId={testId}>
    <input
      type={type}
      name={name}
      required={required}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={`input-${name}`}
      className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors bg-white"
    />
  </FieldWrapper>
);

export const TextareaField = ({ label, required, helper, value, onChange, placeholder, name, rows = 5, testId }) => (
  <FieldWrapper label={label} required={required} helper={helper} testId={testId}>
    <textarea
      name={name}
      required={required}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      data-testid={`textarea-${name}`}
      className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors bg-white resize-y"
    />
  </FieldWrapper>
);

export const SelectField = ({ label, required, helper, value, onChange, options, placeholder = 'Select an option', name, testId }) => (
  <FieldWrapper label={label} required={required} helper={helper} testId={testId}>
    <select
      name={name}
      required={required}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      data-testid={`select-${name}`}
      className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors bg-white"
    >
      <option value="" disabled>{placeholder}</option>
      {options.map((opt) => (
        <option key={opt} value={opt}>{opt}</option>
      ))}
    </select>
  </FieldWrapper>
);

export const MultiSelectField = ({ label, required, helper, values, onChange, options, name, columns = 2, testId }) => {
  const toggle = (opt) => {
    const current = values || [];
    if (current.includes(opt)) {
      onChange(current.filter((v) => v !== opt));
    } else {
      onChange([...current, opt]);
    }
  };
  const gridCls =
    columns === 3 ? 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2'
    : columns === 2 ? 'grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2'
    : 'space-y-2';
  return (
    <FieldWrapper label={label} required={required} helper={helper} testId={testId}>
      <div className={`${gridCls} border border-gray-200 rounded-md p-3 bg-gray-50 max-h-72 overflow-y-auto`} data-testid={`multiselect-${name}`}>
        {options.map((opt) => {
          const checked = (values || []).includes(opt);
          return (
            <label key={opt} className="flex items-start gap-2 text-sm text-gray-800 cursor-pointer hover:text-orange-600 transition-colors">
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(opt)}
                data-testid={`checkbox-${name}-${opt.replace(/[^a-zA-Z0-9]/g, '-').slice(0, 40)}`}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
              />
              <span className="leading-snug">{opt}</span>
            </label>
          );
        })}
      </div>
      {(values || []).length > 0 && (
        <p className="text-xs text-teal-700 font-medium">{(values || []).length} selected</p>
      )}
    </FieldWrapper>
  );
};

export const FileUploadField = ({ label, required, helper, files, onChange, name, testId }) => {
  const inputRef = useRef(null);
  const [error, setError] = useState('');

  const handleAdd = (newFiles) => {
    setError('');
    const list = Array.from(newFiles || []);
    const valid = [];
    for (const f of list) {
      if (f.size > MAX_FILE_SIZE_BYTES) {
        setError(`"${f.name}" exceeds 25 MB limit.`);
        continue;
      }
      valid.push(f);
    }
    onChange([...(files || []), ...valid]);
  };

  const handleRemove = (idx) => {
    const next = (files || []).filter((_, i) => i !== idx);
    onChange(next);
  };

  return (
    <FieldWrapper label={label} required={required} helper={helper} testId={testId}>
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); }}
        onDrop={(e) => { e.preventDefault(); handleAdd(e.dataTransfer.files); }}
        className="cursor-pointer border-2 border-dashed border-gray-300 rounded-md p-6 text-center hover:border-orange-500 hover:bg-orange-50/40 transition-colors"
        data-testid={`file-dropzone-${name}`}
      >
        <Upload className="mx-auto text-gray-400 mb-2" size={24} />
        <p className="text-sm text-gray-700 font-medium">Drop files here or click to browse</p>
        <p className="text-xs text-gray-500 mt-1">PDF, DOC, DOCX, XLS, XLSX, CDX, CDXML, MOL, SDF · Max 25 MB per file</p>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPTED_FILE_TYPES}
          onChange={(e) => handleAdd(e.target.files)}
          className="hidden"
          data-testid={`file-input-${name}`}
        />
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
      {(files || []).length > 0 && (
        <ul className="space-y-1.5 mt-2">
          {files.map((f, idx) => (
            <li key={`${f.name}-${idx}`} className="flex items-center justify-between text-sm bg-gray-50 border border-gray-200 rounded px-3 py-1.5">
              <span className="flex items-center gap-2 text-gray-800 truncate">
                <FileText size={14} className="text-teal-600 flex-shrink-0" />
                <span className="truncate">{f.name}</span>
                <span className="text-xs text-gray-500 flex-shrink-0">({(f.size / 1024 / 1024).toFixed(2)} MB)</span>
              </span>
              <button
                type="button"
                onClick={() => handleRemove(idx)}
                data-testid={`remove-file-${idx}`}
                className="text-gray-400 hover:text-red-600 transition-colors flex-shrink-0"
              >
                <X size={16} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </FieldWrapper>
  );
};

export const Section = ({ title, children }) => (
  <div className="pb-6 border-b border-gray-200 last:border-b-0">
    <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-orange-600 mb-4">{title}</h2>
    <div className="space-y-5">{children}</div>
  </div>
);

// Renders a text input conditionally when `show` is true. Required when visible.
export const ConditionalText = ({ show, label = 'Please specify', helper = 'Please describe your selection.', placeholder = '', value, onChange, name }) => {
  if (!show) return null;
  return (
    <div className="ml-0 mt-[-8px] pl-3 border-l-2 border-orange-400 bg-orange-50/40 rounded-r-md py-2 pr-3" data-testid={`conditional-${name}`}>
      <label className="block text-sm font-semibold text-gray-900">
        {label}<span className="text-orange-600 ml-1">*</span>
      </label>
      <p className="text-xs text-gray-500 leading-snug mb-1">{helper}</p>
      <input
        type="text"
        required
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={`input-${name}`}
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white"
      />
    </div>
  );
};

// Consent & data protection block. `variant` = "customer" | "partner".
export const ConsentSection = ({ variant = 'customer', consent1, consent2, onChange1, onChange2, showError }) => {
  const customerText = "I consent to LAMKO collecting and processing the information submitted in this form, including personal details, technical specifications, and uploaded documents, for the purpose of evaluating and responding to my inquiry. I understand my data will be retained for up to 3 years, treated as confidential, and I may request access, correction, or deletion at any time by contacting privacy@lamko.co.kr.";
  const customerOpt = "I agree to receive occasional updates from LAMKO about relevant services and industry developments. I can unsubscribe at any time.";
  const partnerText = "I confirm that I am authorized to submit this information on behalf of my organization. I consent to LAMKO collecting and processing the submitted company and personal data, including capabilities, certifications, and uploaded documents, for the purpose of evaluating partnership suitability. I understand this data will be retained for up to 5 years, treated as confidential, and I may request access, correction, or deletion at any time by contacting privacy@lamko.co.kr.";
  const partnerOpt = "I agree to receive partnership opportunities, RFQ invitations, and industry updates from LAMKO. I can unsubscribe at any time.";
  const required = variant === 'partner' ? partnerText : customerText;
  const optional = variant === 'partner' ? partnerOpt : customerOpt;

  return (
    <Section title="Consent & Data Protection">
      <label className="flex items-start gap-3 text-sm text-gray-800 cursor-pointer">
        <input
          type="checkbox"
          checked={!!consent1}
          onChange={(e) => onChange1(e.target.checked)}
          data-testid="consent-required"
          className="mt-1 h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500 flex-shrink-0"
        />
        <span className="leading-relaxed">
          {required} <span className="text-orange-600 font-semibold">*</span>
        </span>
      </label>
      {showError && !consent1 && (
        <p className="text-xs text-red-600 ml-7" data-testid="consent-error">Required - please tick the consent box to submit.</p>
      )}
      <label className="flex items-start gap-3 text-sm text-gray-800 cursor-pointer">
        <input
          type="checkbox"
          checked={!!consent2}
          onChange={(e) => onChange2(e.target.checked)}
          data-testid="consent-optional"
          className="mt-1 h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500 flex-shrink-0"
        />
        <span className="leading-relaxed">{optional}</span>
      </label>
      <p className="text-xs text-gray-500 leading-relaxed">
        LAMKO Co., Ltd. protects your data in accordance with applicable data protection laws, including the Korean Personal Information Protection Act (PIPA), EU GDPR, and other relevant international regulations.{' '}
        <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-teal-700 hover:underline font-medium">Privacy Policy</a>
      </p>
    </Section>
  );
};
